// use-feedback-keyboard — global hotkey for the subjective-feedback panel.
//
// 19BM-F: Cmd+/ (mac) / Ctrl+/ (win/linux) toggles the FeedbackPanel. The hook
// stays mounted at the App root so the hotkey works on every route (including
// /course/* which doesn't render MainLayout).
//
// Convention (CLAUDE.md "keyboard-first"): opens with isTypingTarget(e) so that
// typing "/" inside the feedback textarea doesn't re-trigger the panel. Also
// Esc closes when the panel is open (delegated to the panel itself for focus
// management, but the hotkey hook ignores Esc to avoid double-handling).

import { useEffect } from 'react';
import { isTypingTarget } from '../keyboard.js';

/**
 * Bind Cmd+/ (mac) / Ctrl+/ (win/linux) to toggle the feedback panel.
 *
 * @param isOpen  current open state — when open, the hotkey toggles it closed.
 * @param onToggle  called with no args; the caller decides open↔close.
 */
export function useFeedbackKeyboard(isOpen: boolean, onToggle: () => void): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      // Cmd+/ on mac, Ctrl+/ elsewhere. Accept both regardless of platform so
      // a mac user with a non-Apple keyboard (or a win user pressing Cmd on a
      // hackintosh) isn't stranded.
      if ((e.metaKey || e.ctrlKey) && (e.key === '/' || e.code === 'Slash')) {
        e.preventDefault();
        onToggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onToggle]);
}
